/******************************************************************************
 * Created Date: Tuesday March 30th 2021                                      *
 * Author: Sean W.                                                            *
 * -----                                                                      *
 * Last Modified: Fri Apr 29 2022                                             *
 * Modified By: Sean W.                                                       *
 * -----                                                                      *
 * File: /src/components/algorithms/frames/LongProcesses.js                   *
 * Copyright (c) 2021 Kuva                                                    *
 ******************************************************************************/
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import {
  Paper,
  IconButton,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TableHead,
  Typography,
  CircularProgress
} from "@mui/material";
import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  Check as CheckIcon,
  Autorenew as InProgIcon,
  Error as ErrorIcon,
  InfoOutlined as InfoIcon
} from "@mui/icons-material";
import { blue, yellow } from "@mui/material/colors";
import { useParams } from "react-router-dom";

import { useOrganizations } from "../../../contentProviders/OrganizationsContextProvider";
import { getLongTaskTypes } from "../../../utils/dataUtils";
import CameraAPI from "../../../apis/CameraAPI";

import LongTasksTypeSelect from "./LongTasksTypeSelect";
import Row from "./LongProcessTableRow";

const LongProcesses = ({ cameras }) => {
  const { selectedOrg } = useOrganizations();

  let { camera } = useParams();

  const [longTasks, setLongTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [longTaskTypes, setLongTaskTypes] = useState(["All"]);
  const [selectedType, setSelectedType] = useState("All");
  const [filteredTasks, setFilteredTasks] = useState([]);

  // Removes all the status rows of cameras that do not belong to this org
  const filterByOrg = tasks => {
    const cameraIds = cameras
      .filter(
        twin =>
          !twin.orgUUID ||
          twin.orgUUID === selectedOrg?.id ||
          selectedOrg?.id === "all"
      )
      .filter(twin => !twin.deviceId || !camera || twin.deviceId === camera)
      .map(c => c.deviceId);

    return tasks.filter(lt => cameraIds.includes(lt.deviceId)).slice(0, 20);
  };
  const updateLongTasks = async () => {
    try {
      setLongTasks([]);
      setLoading(true);
      const tasks = await CameraAPI.getLongTasks(camera);
      setLongTasks(filterByOrg(tasks.data));
      setLoading(false);
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  };

  useEffect(() => {
    updateLongTasks(camera);
  }, [camera, cameras.length]);

  // Update longtask types upon longtask changes
  useEffect(() => {
    setLongTaskTypes(getLongTaskTypes(longTasks));
  }, [longTasks]);

  // Update filtered tasks upon selectedType and/or longTasks changes
  useEffect(() => {
    if (selectedType === "All") {
      setFilteredTasks([...longTasks]);
    } else {
      const filtered = longTasks.filter(task => task.type === selectedType);
      setFilteredTasks(filtered);
    }
  }, [longTasks, selectedType]);

  return (
    <TableContainer component={Paper}>
      <LongTasksTypeSelect
        types={longTaskTypes}
        selectedType={selectedType}
        setSelectedType={setSelectedType}
      />
      <Table aria-label="collapsible table">
        <TableHead>
          <TableRow>
            <TableCell>
              <IconButton
                aria-label="update"
                onClick={updateLongTasks}
                size="large"
              >
                {loading ? (
                  <CircularProgress style={{ color: yellow[500] }} />
                ) : (
                  <InProgIcon style={{ fontSize: 40, color: blue[500] }} />
                )}
              </IconButton>{" "}
            </TableCell>
            <TableCell>
              <InfoIcon style={{ fontSize: 40 }} />
            </TableCell>
            {/* <TableCell align="right">Status</TableCell> */}
            <TableCell align="right">Camera</TableCell>
            <TableCell align="right">Started</TableCell>
            <TableCell align="right">Finished</TableCell>
            <TableCell align="right">Took</TableCell>
            <TableCell align="right">Link</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredTasks.map(row => (
            <Row key={row.id} row={row} />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

LongProcesses.propTypes = {
  cameras: PropTypes.array.isRequired
};

const mapStateToProps = (state, ownProps) => {
  return {
    cameras: state.cameras
  };
};

export default connect(mapStateToProps)(LongProcesses);
